import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { StatusDot } from "../components/ui/StatusDot";
import { MetricBand } from "../components/ui/MetricBand";

interface PendingProvider {
  id: string;
  name: string;
  website?: string;
  docsUrl?: string;
  pricingUrl?: string;
  notes?: string;
  proposedAt: string;
  sourceQuery: string;
}

interface ProviderScrapeRow {
  id: string;
  name: string;
  url: string;
  method: string;
  status: "ok" | "fail" | "skipped" | "pending" | "running";
  error?: string;
  modelsUpdated?: number;
  /** Merged catalog model count for this providerId. */
  modelsTracked?: number;
}

interface CatalogSyncProviderProgress {
  id: string;
  name?: string;
  status: "pending" | "running" | "ok" | "fail" | "skip";
  phase?: string;
  imported?: number;
  updatedPrices?: number;
  modelsTracked?: number;
  method?: string;
  error?: string;
}

interface CatalogSyncProgress {
  instanceId: string;
  status: "running" | "complete" | "errored";
  mode: "discover" | "prices" | "full";
  step: string;
  phase: "discover" | "prices" | "finalize" | "done";
  providerId?: string;
  providerName?: string;
  index: number;
  total: number;
  imported: number;
  updatedPrices: number;
  tracked?: number;
  providers: CatalogSyncProviderProgress[];
  note?: string;
  updatedAt: string;
}

interface ScrapeSummary {
  ok?: boolean;
  providersAttempted?: number;
  providersSucceeded?: string[];
  providersFailed?: string[];
  modelsUpdated?: number;
  modelsUnchanged?: number;
  modelsUnknown?: number;
  note?: string;
  error?: string;
  browserBound?: boolean;
  providers?: ProviderScrapeRow[];
  browser?: {
    mode?: string;
    pagesRendered?: number;
    methods?: Record<string, number>;
    msUsedTotal?: number;
  };
}

type ScrapePhase = "idle" | "running" | "done";

function formatRequestedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-GB", {
    timeZone: "Asia/Calcutta",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    timeZone: "Asia/Calcutta",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date}, ${time} IST`;
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url || "—";
  }
}

function statusChipClass(status: ProviderScrapeRow["status"]): string {
  if (status === "ok") return "admin-chip ok";
  if (status === "fail") return "admin-chip fail";
  if (status === "skipped") return "admin-chip skip";
  if (status === "running") return "admin-chip run";
  return "admin-chip";
}

function statusLabel(status: ProviderScrapeRow["status"]): string {
  if (status === "ok") return "Ok";
  if (status === "fail") return "Failed";
  if (status === "skipped") return "Skipped";
  if (status === "running") return "Running";
  return "Pending";
}

function mergeProviderRows(
  base: ProviderScrapeRow[],
  updates: ProviderScrapeRow[],
): ProviderScrapeRow[] {
  if (!updates.length) return base;
  const byId = new Map(base.map((r) => [r.id, r]));
  for (const u of updates) {
    const prev = byId.get(u.id);
    byId.set(
      u.id,
      prev
        ? {
            ...prev,
            ...u,
            name: u.name || prev.name,
            url: u.url || prev.url,
            modelsTracked: u.modelsTracked ?? prev.modelsTracked,
          }
        : u,
    );
  }
  // Keep base order; append any new ids from updates.
  const seen = new Set<string>();
  const out: ProviderScrapeRow[] = [];
  for (const r of base) {
    out.push(byId.get(r.id)!);
    seen.add(r.id);
  }
  for (const u of updates) {
    if (!seen.has(u.id)) out.push(byId.get(u.id)!);
  }
  return out;
}

function mapProgressStatus(
  status: CatalogSyncProviderProgress["status"],
): ProviderScrapeRow["status"] {
  if (status === "skip") return "skipped";
  return status;
}

function progressRowDetail(p: CatalogSyncProviderProgress): string | undefined {
  if (p.error) return p.error;
  if (p.status === "running") {
    if (p.phase === "prices") return "Scraping…";
    if (p.phase === "discover") return "Syncing models…";
    return "Syncing…";
  }
  if (p.status === "pending") return undefined;
  const bits: string[] = [];
  if (p.imported != null && p.imported > 0) bits.push(`Imported ${p.imported}`);
  if (p.updatedPrices != null) bits.push(`Updated prices ${p.updatedPrices}`);
  if (p.modelsTracked != null) bits.push(`Tracked ${p.modelsTracked}`);
  return bits.length ? bits.join(" · ") : undefined;
}

function rowsFromProgress(
  progress: CatalogSyncProgress,
  base: ProviderScrapeRow[],
): ProviderScrapeRow[] {
  const updates: ProviderScrapeRow[] = progress.providers.map((p) => {
    const prev = base.find((r) => r.id === p.id);
    return {
      id: p.id,
      name: p.name || prev?.name || p.id,
      url: prev?.url ?? "",
      method: p.method || prev?.method || "—",
      status: mapProgressStatus(p.status),
      error: progressRowDetail(p) ?? (p.status === "pending" ? undefined : prev?.error),
      modelsUpdated: p.updatedPrices ?? prev?.modelsUpdated,
      modelsTracked: p.modelsTracked ?? prev?.modelsTracked,
    };
  });
  return mergeProviderRows(base.length ? base : updates, updates);
}

function formatProgressStrip(progress: CatalogSyncProgress): string {
  const label =
    progress.status === "complete"
      ? "Complete"
      : progress.status === "errored"
        ? "Errored"
        : "Running";
  const who = progress.providerName || progress.providerId;
  const idx =
    progress.total > 0
      ? `${Math.min(progress.index + 1, progress.total)}/${progress.total}`
      : "";
  const parts = [label];
  if (who) parts.push(`${who}${idx ? ` (${idx})` : ""}`);
  if (progress.phase && progress.phase !== "done") parts.push(progress.phase);
  parts.push(`imported ${progress.imported}`);
  parts.push(`prices ${progress.updatedPrices}`);
  if (progress.tracked != null) parts.push(`tracked ${progress.tracked}`);
  return parts.join(" · ");
}

function rateLimitHint(error?: string): string | undefined {
  if (!error || !/rate\s*limit/i.test(error)) return undefined;
  return "Browser Run rate limit — retry in a minute, or add REPLICATE_API_TOKEN";
}

function rowDetail(row: ProviderScrapeRow): string {
  const tracked =
    row.modelsTracked != null && row.modelsTracked >= 0
      ? row.modelsTracked
      : undefined;
  const trackedBit =
    tracked != null ? ` · Tracked ${tracked}` : "";
  if (row.status === "running") {
    return row.error || "Fetching…";
  }
  if (row.status === "fail" || row.status === "skipped") {
    const err = row.error || "—";
    const hint = rateLimitHint(row.error);
    return hint ? `${err} — ${hint}` : err;
  }
  if (row.status === "ok") {
    if (row.error && /imported|tracked/i.test(row.error)) {
      return `${row.error}${trackedBit && !/tracked/i.test(row.error) ? trackedBit : ""}`;
    }
    if (row.error && /no price change/i.test(row.error)) {
      return tracked != null ? `Updated prices 0${trackedBit}` : "No price change";
    }
    if (row.modelsUpdated != null && row.modelsUpdated > 0) {
      return `Updated prices ${row.modelsUpdated}${trackedBit}`;
    }
    if (row.modelsUpdated === 0 && tracked != null) {
      return `Updated prices 0${trackedBit}`;
    }
    if (row.error) return row.error;
    return tracked != null ? `Updated prices 0${trackedBit}` : "No price change";
  }
  return "—";
}

export function AdminPage() {
  const { unlocked, loading } = useAuth();
  const [pending, setPending] = useState<PendingProvider[]>([]);
  const [drafts, setDrafts] = useState<Record<string, PendingProvider>>({});
  const [scrapeTargets, setScrapeTargets] = useState<ProviderScrapeRow[]>([]);
  const [providerRows, setProviderRows] = useState<ProviderScrapeRow[]>([]);
  const [lastScrape, setLastScrape] = useState<ScrapeSummary | null>(null);
  const [phase, setPhase] = useState<ScrapePhase>("idle");
  const [busy, setBusy] = useState(false);
  const [runningIds, setRunningIds] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<CatalogSyncProgress | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const refreshPending = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/pending-providers", {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        pending?: PendingProvider[];
        scrapeProviders?: ProviderScrapeRow[] | null;
        scrapeTargets?: ProviderScrapeRow[] | null;
        scrapeSummary?: string | null;
        lastPriceScrapeAt?: string | null;
      };
      const list = data.pending ?? [];
      setPending(list);
      setDrafts(Object.fromEntries(list.map((p) => [p.id, { ...p }])));
      const targets = data.scrapeTargets ?? [];
      if (targets.length) setScrapeTargets(targets);
      const last = data.scrapeProviders ?? [];
      if (last.length) {
        // Server already merges last-run onto targets when present.
        setProviderRows(last);
        setLastScrape((prev) =>
          prev ?? {
            providers: last,
            note: data.scrapeSummary ?? undefined,
            modelsUpdated: undefined,
          },
        );
        setPhase("done");
      } else if (targets.length) {
        setProviderRows(targets);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (unlocked) void refreshPending();
  }, [unlocked, refreshPending]);

  const trackedById = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of scrapeTargets) {
      if (t.modelsTracked != null) m.set(t.id, t.modelsTracked);
    }
    for (const r of providerRows) {
      if (r.modelsTracked != null && !m.has(r.id)) m.set(r.id, r.modelsTracked);
    }
    return m;
  }, [scrapeTargets, providerRows]);

  const withTracked = (r: ProviderScrapeRow): ProviderScrapeRow => ({
    ...r,
    modelsTracked: trackedById.get(r.id) ?? r.modelsTracked,
  });

  const displayRows = useMemo(() => {
    const base = providerRows.length
      ? providerRows
      : scrapeTargets.length
        ? scrapeTargets
        : [];
    // When Workflow progress is streaming, trust per-row statuses from providerRows.
    if (phase === "running" && runningIds.length === 0 && !syncProgress) {
      // Full-table scrape (no live progress yet): all rows running.
      return base.map((r) =>
        withTracked({
          ...r,
          status: "running" as const,
          error: r.error && r.status === "running" ? r.error : "Fetching…",
          method: r.method === "—" ? r.method : r.method,
        }),
      );
    }
    if (runningIds.length) {
      const run = new Set(runningIds);
      return base.map((r) =>
        withTracked(
          run.has(r.id)
            ? {
                ...r,
                status: "running" as const,
                error: r.error || "Fetching…",
              }
            : r,
        ),
      );
    }
    return base.map(withTracked);
  }, [phase, providerRows, scrapeTargets, runningIds, trackedById, syncProgress]);

  const summary = useMemo(() => {
    const rows = providerRows.length ? providerRows : (lastScrape?.providers ?? []);
    const updated =
      lastScrape?.modelsUpdated ??
      rows.filter((r) => r.status === "ok").reduce((n, r) => n + (r.modelsUpdated ?? 0), 0);
    const unchanged = lastScrape?.modelsUnchanged;
    const failed = rows.filter((r) => r.status === "fail");
    const rendered = lastScrape?.browser?.pagesRendered;
    return { updated, unchanged, failed, rendered, rows };
  }, [lastScrape, providerRows]);

  if (loading) return <p className="muted">Checking session…</p>;
  if (!unlocked) return <Navigate to="/" replace />;


  async function pollWorkflow(id: string) {
    setWorkflowId(id);
    setSyncProgress(null);
    for (let i = 0; i < 200; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const res = await fetch(`/api/admin/catalog-sync/${id}`, { credentials: "include" });
        const data = (await res.json()) as {
          status?: string;
          output?: { note?: string; imported?: number; updatedPrices?: number; tracked?: number };
          summary?: { note?: string; imported?: number; updatedPrices?: number; tracked?: number };
          progress?: CatalogSyncProgress | null;
          error?: string | { message?: string };
        };
        const progress = data.progress ?? null;
        if (progress) {
          setSyncProgress(progress);
          setWorkflowStatus(progress.status);
          if (progress.note) setSyncNote(progress.note);
          else if (progress.step) setSyncNote(progress.step);
          setProviderRows((prev) => {
            const base = prev.length ? prev : scrapeTargets;
            return rowsFromProgress(progress, base);
          });
          const running = progress.providers
            .filter((p) => p.status === "running")
            .map((p) => p.id);
          setRunningIds(running);
        } else {
          const st = data.status ?? "unknown";
          setWorkflowStatus(st);
        }

        const note =
          progress?.note ??
          data.output?.note ??
          data.summary?.note ??
          (typeof data.error === "string" ? data.error : data.error?.message);
        if (note) setSyncNote(note);

        const st = progress?.status ?? data.status ?? "unknown";
        const done =
          st === "complete" ||
          st === "errored" ||
          data.status === "complete" ||
          data.status === "errored" ||
          data.status === "terminated";
        if (done) {
          setWorkflowStatus(data.status ?? st);
          if (progress?.status === "complete" || data.status === "complete") {
            setMsg(note ?? `Workflow ${id} complete`);
            setPhase("done");
          } else {
            setMsg(`Workflow ${data.status ?? st}: ${note ?? id}`);
            setPhase("done");
          }
          setRunningIds([]);
          setBusy(false);
          await refreshPending();
          return;
        }
      } catch {
        /* keep polling */
      }
    }
    setMsg(`Workflow ${id} still running — check back`);
    setRunningIds([]);
    setBusy(false);
  }

  async function runCatalogSync(
    kind: "full" | "discover" | "prices",
    providerIds?: string[],
  ) {
    const single = Boolean(providerIds?.length);
    setMsg(null);
    setSyncNote(null);
    setSyncProgress(null);
    if (single && providerIds) {
      setRunningIds((prev) => [...new Set([...prev, ...providerIds])]);
      setProviderRows((prev) => {
        const base = prev.length ? prev : scrapeTargets;
        return mergeProviderRows(
          base,
          providerIds.map((id) => {
            const prevRow = base.find((r) => r.id === id);
            return {
              id,
              name: prevRow?.name ?? id,
              url: prevRow?.url ?? "",
              method: prevRow?.method ?? "—",
              status: "running" as const,
              error: kind === "discover" ? "Syncing models…" : "Scraping…",
              modelsUpdated: undefined,
              modelsTracked: prevRow?.modelsTracked,
            };
          }),
        );
      });
    } else {
      setBusy(true);
      setPhase("running");
      setRunningIds([]);
    }
    try {
      // Full / multi-provider → Workflow; single price-only can stay inline.
      const useWorkflow = !(kind === "prices" && single);
      const res = await fetch("/api/admin/catalog-sync", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: kind === "full" ? "full" : kind === "discover" ? "discover" : "prices",
          providerIds,
          useBrowser: "hybrid",
          useJev: true,
          inline: !useWorkflow,
          maxModels: 400,
          maxPages: 20,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        workflow?: boolean;
        inline?: boolean;
        id?: string;
        status?: string;
        error?: string;
        note?: string;
        modelsUpdated?: number;
        providers?: ProviderScrapeRow[];
        replicate?: { imported?: number; tracked?: number; note?: string };
      };
      if (!res.ok) {
        setMsg(data.error ?? "Catalog sync failed");
        return;
      }
      if (data.workflow && data.id) {
        setMsg(`Started workflow ${data.id} (${data.status ?? "queued"})`);
        await pollWorkflow(data.id);
        return;
      }
      // Inline result
      if (data.providers?.length) {
        setProviderRows((prev) =>
          mergeProviderRows(prev.length ? prev : scrapeTargets, data.providers!),
        );
      }
      if (data.replicate?.note) setSyncNote(data.replicate.note);
      setMsg(
        data.note ??
          data.replicate?.note ??
          (data.modelsUpdated != null
            ? `Updated ${data.modelsUpdated}`
            : "Sync finished"),
      );
      setPhase("done");
      await refreshPending();
    } catch {
      setMsg("Catalog sync request failed");
    } finally {
      if (single && providerIds) {
        setRunningIds((prev) => prev.filter((id) => !providerIds.includes(id)));
      } else {
        setRunningIds([]);
        setBusy(false);
      }
    }
  }


  async function runScrape(
    kind: "hybrid" | "browser",
    providerIds?: string[],
  ) {
    const single = Boolean(providerIds?.length);
    setMsg(null);

    const baseSnapshot =
      providerRows.length > 0
        ? providerRows
        : scrapeTargets.length
          ? scrapeTargets
          : [];

    if (single && providerIds) {
      // Row-lock only: track this provider; do not set global busy.
      setRunningIds((prev) => [...new Set([...prev, ...providerIds])]);
      setProviderRows((prev) => {
        const base = prev.length ? prev : baseSnapshot;
        const fallback: ProviderScrapeRow[] =
          base.length > 0
            ? base
            : providerIds.map((id) => ({
                id,
                name: id,
                url: "",
                method: "—",
                status: "pending" as const,
              }));
        return mergeProviderRows(
          fallback,
          providerIds.map((id) => {
            const prevRow = fallback.find((r) => r.id === id);
            return {
              id,
              name: prevRow?.name ?? id,
              url: prevRow?.url ?? "",
              method: prevRow?.method ?? "—",
              status: "running" as const,
              error: kind === "browser" ? "Browser rendering…" : "Fetching…",
              modelsUpdated: undefined,
              modelsTracked: prevRow?.modelsTracked,
            };
          }),
        );
      });
    } else {
      setBusy(true);
      setPhase("running");
      setRunningIds([]);
      const planned = (baseSnapshot.length ? baseSnapshot : scrapeTargets).map(
        (t) => ({
          ...t,
          status: "running" as const,
          error: "Fetching…",
        }),
      );
      if (planned.length) setProviderRows(planned);
    }

    try {
      const path =
        kind === "browser"
          ? "/api/admin/scrape-prices-browser"
          : "/api/admin/scrape-prices";
      const body =
        kind === "browser"
          ? {
              useJev: true,
              mode: "browser" as const,
              ...(providerIds?.length ? { providerIds } : {}),
            }
          : {
              useJev: true,
              useBrowser: "hybrid" as const,
              ...(providerIds?.length ? { providerIds } : {}),
            };
      const res = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as ScrapeSummary;

      if (single) {
        // Patch only returned rows; keep other providers' last-run status.
        if (data.providers?.length) {
          setProviderRows((prev) =>
            mergeProviderRows(prev.length ? prev : baseSnapshot, data.providers!),
          );
        } else if (providerIds) {
          // Ensure we don't leave Running if API omitted providers[].
          setProviderRows((prev) =>
            mergeProviderRows(
              prev.length ? prev : baseSnapshot,
              providerIds.map((id) => ({
                id,
                name: baseSnapshot.find((r) => r.id === id)?.name ?? id,
                url: baseSnapshot.find((r) => r.id === id)?.url ?? "",
                method: "—",
                status: "fail" as const,
                error: data.error ?? "No provider result returned",
              })),
            ),
          );
        }
        setLastScrape((prev) => ({
          ...(prev ?? {}),
          ...data,
          providers: undefined, // keep table from last merge, not partial list
        }));
        setPhase("done");
      } else {
        setLastScrape(data);
        if (data.providers?.length) {
          setProviderRows(data.providers);
        } else {
          // Don't leave everything PENDING/Running after a completed full run.
          setProviderRows((prev) =>
            prev.map((r) =>
              r.status === "running"
                ? {
                    ...r,
                    status: "fail" as const,
                    error: data.error ?? "No provider results returned",
                  }
                : r,
            ),
          );
        }
        setPhase("done");
      }

      if (!res.ok) setMsg(data.error ?? "Price refresh failed");
      else if (single && providerIds) {
        const row = data.providers?.find((p) => p.id === providerIds[0]);
        const name = row?.name ?? providerIds[0];
        if (row?.status === "ok") {
          const tracked =
            row.modelsTracked != null ? ` / tracked ${row.modelsTracked}` : "";
          setMsg(
            row.modelsUpdated && row.modelsUpdated > 0
              ? `${name}: updated ${row.modelsUpdated}${tracked}`
              : `${name}: updated 0${tracked || " — no price change"}`,
          );
        } else if (row) {
          const err = row.error ?? row.status;
          const hint =
            typeof err === "string" && /rate\s*limit/i.test(err)
              ? " — Browser Run rate limit — retry in a minute, or add REPLICATE_API_TOKEN"
              : "";
          setMsg(`${name}: ${err}${hint}`);
        }
      }
    } catch {
      setMsg("Price refresh request failed");
      if (single && providerIds) {
        setProviderRows((prev) =>
          mergeProviderRows(
            prev,
            providerIds.map((id) => ({
              id,
              name: prev.find((r) => r.id === id)?.name ?? id,
              url: prev.find((r) => r.id === id)?.url ?? "",
              method: prev.find((r) => r.id === id)?.method ?? "—",
              status: "fail" as const,
              error: "Request failed",
            })),
          ),
        );
        setPhase("done");
      } else {
        setPhase("idle");
      }
    } finally {
      if (single && providerIds) {
        setRunningIds((prev) => prev.filter((id) => !providerIds.includes(id)));
      } else {
        setRunningIds([]);
        setBusy(false);
      }
    }
  }

  async function saveDraft(id: string) {
    const draft = drafts[id];
    if (!draft) return;
    setSavingId(id);
    setMsg(null);
    try {
      const res = await fetch(`/api/admin/pending-providers/${id}/update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: draft.name,
          website: draft.website ?? "",
          docsUrl: draft.docsUrl ?? "",
          pricingUrl: draft.pricingUrl ?? "",
          notes: draft.notes ?? "",
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        error?: string;
        pending?: PendingProvider;
      };
      if (!res.ok || !data.ok) {
        setMsg(data.error ?? "Could not save links");
      } else if (data.pending) {
        setPending((list) => list.map((p) => (p.id === id ? data.pending! : p)));
        setDrafts((d) => ({ ...d, [id]: { ...data.pending! } }));
        setMsg(`Saved links for ${data.pending.name}`);
      }
    } catch {
      setMsg("Save failed");
    } finally {
      setSavingId(null);
    }
  }

  async function decide(id: string, action: "approve" | "reject") {
    setBusy(true);
    setMsg(null);
    try {
      if (action === "approve" && drafts[id]) {
        await fetch(`/api/admin/pending-providers/${id}/update`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: drafts[id].name,
            website: drafts[id].website ?? "",
            docsUrl: drafts[id].docsUrl ?? "",
            pricingUrl: drafts[id].pricingUrl ?? "",
            notes: drafts[id].notes ?? "",
          }),
        });
      }
      const res = await fetch(`/api/admin/pending-providers/${id}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) setMsg(data.error ?? `${action} failed`);
      else {
        const name = drafts[id]?.name ?? id;
        setMsg(action === "approve" ? `Approved ${name}` : `Rejected ${name}`);
      }
      await refreshPending();
    } catch {
      setMsg(`${action} failed`);
    } finally {
      setBusy(false);
    }
  }

  function patchDraft(id: string, patch: Partial<PendingProvider>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  }

  const phaseKind =
    phase === "running" || runningIds.length
      ? "running"
      : phase === "done"
        ? "ran"
        : "synced";
  const phaseLabel =
    phase === "running" || runningIds.length
      ? "Running"
      : phase === "done"
        ? "Done"
        : "Idle";
  const okCount =
    lastScrape?.providersSucceeded?.length ??
    summary.rows.filter((r) => r.status === "ok").length;
  const fullTableRunning = phase === "running" && runningIds.length === 0;
  const anyRunning = busy || fullTableRunning || runningIds.length > 0;

  return (
    <div className="admin-page">
      <p className="eyebrow">ADMIN / CONTROL</p>
      <div className="page-header-row" style={{ marginBottom: "0.85rem" }}>
        <h1 className="tl-title" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}>
          Admin
          <span className="ctx"> / pricing · provider requests</span>
        </h1>
        <StatusDot kind="live" label="PIN SESSION" />
      </div>

      <MetricBand
        items={[
          { label: "Provider requests", value: pending.length },
          {
            label: "Last refresh updated",
            value: lastScrape?.modelsUpdated ?? summary.updated ?? "—",
            tone: "amber",
          },
          {
            label: "Providers ok",
            value: okCount || "—",
          },
          {
            label: "Browser pages",
            value: lastScrape?.browser?.pagesRendered ?? "—",
          },
        ]}
      />

      <section className="intent-band admin-card" style={{ marginTop: "1.25rem" }}>
        <div className="intent-band-head">
          <p className="eyebrow" style={{ marginBottom: 0 }}>
            PRICING SCRAPE
          </p>
          <StatusDot kind={phaseKind} label={phaseLabel} />
        </div>
        <h2 className="admin-section-title">Refresh prices from provider sites</h2>
        <p className="muted admin-lead">
          Pulls public pricing pages and updates catalog USD when a price is clearly listed — never
          invents numbers. Use <strong>Refresh prices</strong> for everyday updates (fast fetch,
          browser only when needed). Use <strong>Force browser</strong> when a site is JS-heavy or
          the hybrid pass missed it. Per-row <strong>Scrape</strong> refreshes prices; <strong>Sync models</strong> discovers + imports the catalog (Cloudflare Workflow).
        </p>
        <div className="cta-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn sm"
            disabled={anyRunning}
            onClick={() => void runScrape("hybrid")}
          >
            {phase === "running" && !runningIds.length ? "Refreshing…" : "Refresh prices"}
          </button>
          <button
            type="button"
            className="btn ghost sm"
            disabled={anyRunning}
            onClick={() => void runScrape("browser")}
          >
            Force browser
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={anyRunning}
            onClick={() => void runCatalogSync("full")}
            title="Discover + import catalogs then refresh prices (Workflow)"
          >
            {phase === "running" && !runningIds.length ? "Syncing…" : "Sync models"}
          </button>
        </div>

        <details className="admin-disclosure">
          <summary>How it works</summary>
          <p className="muted" style={{ margin: "0.55rem 0 0" }}>
            Hybrid tries a plain page fetch first, then Cloudflare Browser Rendering when the page
            is thin or script-driven. Structured extractors run for known providers; otherwise we
            only keep amounts that appear on the page. The daily cron uses the hybrid path.
          </p>
        </details>

        {phase !== "idle" || summary.rows.length > 0 ? (
          <div className="admin-scrape-summary">
            <div className="admin-stat-row">
              <div className="admin-stat">
                <span className="admin-stat-label">Updated</span>
                <span className="admin-stat-value amber">{summary.updated ?? "—"}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">Unchanged</span>
                <span className="admin-stat-value">{summary.unchanged ?? "—"}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">Browser pages</span>
                <span className="admin-stat-value">{summary.rendered ?? "—"}</span>
              </div>
            </div>
            {workflowId ? (
              <div style={{ marginTop: "0.55rem" }}>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Workflow <code>{workflowId}</code>
                  {workflowStatus ? ` · ${workflowStatus}` : ""}
                  {syncNote && !syncProgress ? ` · ${syncNote}` : ""}
                </p>
                {syncProgress ? (
                  <div className="admin-sync-progress" style={{ marginTop: "0.45rem" }}>
                    <p
                      className="muted"
                      style={{ margin: "0 0 0.35rem", fontSize: "0.82rem", lineHeight: 1.35 }}
                    >
                      {formatProgressStrip(syncProgress)}
                    </p>
                    <div
                      className="admin-progress-track"
                      style={{
                        height: 6,
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.08)",
                        overflow: "hidden",
                      }}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={syncProgress.total || 1}
                      aria-valuenow={
                        syncProgress.status === "complete"
                          ? syncProgress.total
                          : Math.min(
                              syncProgress.index +
                                (syncProgress.providers.some((p) => p.status === "running")
                                  ? 1
                                  : 0),
                              syncProgress.total || 1,
                            )
                      }
                    >
                      <div
                        className="admin-progress-fill"
                        style={{
                          height: "100%",
                          width: `${
                            syncProgress.total > 0
                              ? Math.min(
                                  100,
                                  (100 *
                                    (syncProgress.status === "complete"
                                      ? syncProgress.total
                                      : syncProgress.providers.filter((p) =>
                                          ["ok", "fail", "skip"].includes(p.status),
                                        ).length +
                                        (syncProgress.providers.some(
                                          (p) => p.status === "running",
                                        )
                                          ? 0.5
                                          : 0))) /
                                    syncProgress.total,
                                )
                              : 0
                          }%`,
                          background: "var(--accent)",
                          transition: "width 0.35s ease",
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {summary.failed.length > 0 ? (
              <div className="admin-fail-chips">
                <span className="admin-stat-label">Failed</span>
                <div className="admin-chip-row">
                  {summary.failed.map((f) => (
                    <span key={f.id} className="admin-chip fail" title={f.error ?? f.id}>
                      {f.name || f.id}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {displayRows.length > 0 ? (
          <div className="table-wrap admin-scrape-table" style={{ marginTop: "0.85rem" }}>
            <table className="data-grid admin-grid">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Models</th>
                  <th>Pricing URL</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Detail</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => {
                  const detail = rowDetail(row);
                  const rowBusy = row.status === "running" || runningIds.includes(row.id);
                  return (
                    <tr key={row.id} className={rowBusy ? "admin-row-running" : undefined}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="muted admin-provider-meta">
                          {row.modelsTracked != null
                            ? `${row.modelsTracked} tracked in catalog`
                            : "—"}
                          <span className="admin-provider-id"> · {row.id}</span>
                        </div>
                      </td>
                      <td className="admin-models-cell">
                        <span className="admin-models-count">
                          {row.modelsTracked != null ? row.modelsTracked : "—"}
                        </span>
                      </td>
                      <td className="admin-url-cell">
                        {row.url ? (
                          <a href={row.url} target="_blank" rel="noreferrer" title={row.url}>
                            {hostLabel(row.url)}
                          </a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className="admin-method">{row.method || "—"}</span>
                      </td>
                      <td>
                        <span className={statusChipClass(row.status)}>
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td className="muted admin-detail-cell" style={{ fontSize: "0.8rem" }}>
                        <span title={row.error && row.error.length > 40 ? row.error : detail}>
                          {detail.length > 72 ? `${detail.slice(0, 70)}…` : detail}
                        </span>
                      </td>
                      <td className="admin-actions-cell">
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            className="btn sm admin-action-btn"
                            disabled={rowBusy || fullTableRunning}
                            title={`Scrape ${row.name} (hybrid)`}
                            onClick={() => void runScrape("hybrid", [row.id])}
                          >
                            {rowBusy ? "…" : "Scrape"}
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm admin-action-btn"
                            disabled={rowBusy || fullTableRunning}
                            title={`Force browser scrape for ${row.name}`}
                            onClick={() => void runScrape("browser", [row.id])}
                          >
                            Browser
                          </button>
                          <button
                            type="button"
                            className="btn sm admin-action-btn"
                            disabled={rowBusy || fullTableRunning}
                            title={`Discover + import models for ${row.name} (Workflow)`}
                            onClick={() => void runCatalogSync("full", [row.id])}
                          >
                            Sync
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted" style={{ marginTop: "0.75rem" }}>
            No scrape results yet. Refresh prices to see each provider site, method, and status.
          </p>
        )}

        {msg ? (
          <p className="muted" style={{ marginTop: "0.75rem" }}>
            {msg}
          </p>
        ) : null}
      </section>

      <section className="admin-card" style={{ marginTop: "1.75rem" }}>
        <p className="eyebrow">PROVIDER REQUESTS</p>
        <h2 className="admin-section-title">Awaiting approval</h2>
        <p className="muted admin-lead">
          From catalog intent like “add provider Foo” or “track provider https://…”. Edit website,
          docs, and pricing links before you approve — approval uses the saved URLs.
        </p>

        {pending.length === 0 ? (
          <div className="empty admin-empty">
            <div className="empty-icon">⌀</div>
            <strong>No provider requests</strong>
            <p className="muted" style={{ margin: "0.25rem 0 0" }}>
              New requests from intent search will show up here.
            </p>
          </div>
        ) : (
          <div className="admin-pending-list">
            {pending.map((p) => {
              const draft = drafts[p.id] ?? p;
              const dirty =
                (draft.website ?? "") !== (p.website ?? "") ||
                (draft.docsUrl ?? "") !== (p.docsUrl ?? "") ||
                (draft.pricingUrl ?? "") !== (p.pricingUrl ?? "") ||
                draft.name !== p.name;
              return (
                <article key={p.id} className="admin-pending-card">
                  <div className="admin-pending-head">
                    <div>
                      <strong className="admin-pending-name">{draft.name}</strong>
                      <div className="muted" style={{ fontSize: "0.75rem" }}>
                        {p.id} · requested {formatRequestedAt(p.proposedAt)}
                      </div>
                    </div>
                    <div className="cta-row" style={{ gap: "0.35rem" }}>
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy || savingId === p.id || !dirty}
                        onClick={() => void saveDraft(p.id)}
                      >
                        {savingId === p.id ? "Saving…" : "Save links"}
                      </button>
                      <button
                        type="button"
                        className="btn sm"
                        disabled={busy}
                        onClick={() => void decide(p.id, "approve")}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => void decide(p.id, "reject")}
                      >
                        Reject
                      </button>
                    </div>
                  </div>

                  <div className="admin-link-fields">
                    <label className="admin-field">
                      <span>Website</span>
                      <input
                        type="url"
                        value={draft.website ?? ""}
                        placeholder="https://…"
                        onChange={(e) => patchDraft(p.id, { website: e.target.value })}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Docs URL</span>
                      <input
                        type="url"
                        value={draft.docsUrl ?? ""}
                        placeholder="https://…"
                        onChange={(e) => patchDraft(p.id, { docsUrl: e.target.value })}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Pricing URL</span>
                      <input
                        type="url"
                        value={draft.pricingUrl ?? ""}
                        placeholder="https://…"
                        onChange={(e) => patchDraft(p.id, { pricingUrl: e.target.value })}
                      />
                    </label>
                  </div>

                  <p className="admin-source-query muted" title={p.sourceQuery}>
                    Source: {p.sourceQuery}
                  </p>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
